import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { CallbackPage } from "./pages/CallbackPage";
import { ApiStatusPage } from "./pages/ApiStatusPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = (token, userData) => {
    localStorage.setItem('spotify_access_token', token);
    localStorage.setItem('spotify_user', JSON.stringify(userData));
    setAccessToken(token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_user');
    localStorage.removeItem('spotify_refresh_token');
    setAccessToken(null);
    setUser(null);
  };

  // Spotify access tokeny zyja tylko 1h. Bez tego apka po godzinie cicho przestaje
  // dzialac (wszystkie zapytania do Spotify zaczynaja zwracac 401) az user sam sie
  // wyloguje i zaloguje ponownie. Odswiezamy przez zapisany refresh_token.
  const refreshAccessToken = async () => {
    const refreshTokenStored = localStorage.getItem('spotify_refresh_token');
    if (!refreshTokenStored) return false;
    try {
      const res = await fetch(`${API}/auth/refresh?refresh_token=${encodeURIComponent(refreshTokenStored)}`, { method: 'POST' });
      if (!res.ok) return false;
      const data = await res.json();
      if (!data.access_token) return false;
      localStorage.setItem('spotify_access_token', data.access_token);
      setAccessToken(data.access_token);
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const init = async () => {
      const storedToken = localStorage.getItem('spotify_access_token');
      const storedUser = localStorage.getItem('spotify_user');
      const refreshTokenStored = localStorage.getItem('spotify_refresh_token');

      if (storedToken && storedUser) {
        setAccessToken(storedToken);
        setUser(JSON.parse(storedUser));
      }
      // Odswiez od razu przy starcie — nie wiemy jak dawno zapisany token zostal wydany
      if (refreshTokenStored && storedUser) {
        await refreshAccessToken();
      }
      setLoading(false);
    };
    init();
  }, []);

  // Auto-odswiezanie co 45 min dla dlugo otwartych kart (token zyje 1h)
  useEffect(() => {
    if (!accessToken) return;
    const interval = setInterval(() => { refreshAccessToken(); }, 45 * 60 * 1000);
    return () => clearInterval(interval);
  }, [accessToken]);

  return { user, accessToken, loading, login, logout, refreshAccessToken };
};

function App() {
  return (
    <div className="App min-h-screen bg-[#0A0A0A]">
      <Toaster 
        position="top-right" 
        theme="dark"
        toastOptions={{
          style: {
            background: '#121212',
            border: '1px solid #27272A',
            color: '#FAFAFA'
          }
        }}
      />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </div>
  );
}

function AppRoutes() {
  const auth = useAuth();

  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF3366] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={auth.accessToken ? <DashboardPage auth={auth} /> : <LoginPage />} />
      <Route path="/callback" element={<CallbackPage auth={auth} />} />
      <Route path="/dashboard" element={<DashboardPage auth={auth} />} />
      <Route path="/endpoints" element={<ApiStatusPage />} />
    </Routes>
  );
}

export default App;
